import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search,
  Package,
  Plus,
  ArrowLeft,
  Loader2,
  CheckCircle,
  Eye,
  Printer,
  X,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import DataTable from '@/components/DataTable';
import ExportExcelButton from '@/components/ExportExcelButton';
import { fetchClientesByLavanderia } from '@/services/clientes.service';
import { fetchLaundryById } from '@/pages/parametrizacion/laundries/Services/laundries.services';
import { fetchEmployees } from '@/pages/parametrizacion/employees/Services/employees.services';
import { getPrinterInstance } from '@/utils/bluetoothPrinter';
import {
  createRecepcionRopaCliente,
  fetchRecepcionesRopaClientes,
} from '@/services/recepcionRopaClientes.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const RecepcionRopaClientesPage = () => {
  const { idLavanderia } = useParams();
  const { toast } = useToast();
  const { user } = useAuth();

  // Tabla principal
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [lavanderiaNombre, setLavanderiaNombre] = useState('');

  // Modal crear recepción
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [isLoadingClientes, setIsLoadingClientes] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [clienteSearch, setClienteSearch] = useState('');
  const [prendas, setPrendas] = useState([]);
  const [isLoadingPrendas, setIsLoadingPrendas] = useState(false);
  const [empleados, setEmpleados] = useState([]);
  const [isLoadingEmpleados, setIsLoadingEmpleados] = useState(false);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);
  const [empleadoSearch, setEmpleadoSearch] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  // Modal resumen/recibo
  const [isReciboOpen, setIsReciboOpen] = useState(false);
  const [reciboData, setReciboData] = useState(null);

  // Modal detalle
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const printer = getPrinterInstance();

  // Cargar registros
  const loadRecords = useCallback(async () => {
    setIsLoading(true);
    try {
      const query = { 'lavanderia.id': idLavanderia };

      if (searchTerm) {
        query.$or = [
          { 'cliente.nombre': { $regex: searchTerm, $options: 'i' } },
          { consecutivo: { $regex: searchTerm, $options: 'i' } },
          { recibido_por: { $regex: searchTerm, $options: 'i' } },
        ];
      }

      const response = await fetchRecepcionesRopaClientes({
        query,
        limit: itemsPerPage,
        page: currentPage,
      });

      setRecords(response.data || []);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      console.error('Error al cargar recepciones:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las recepciones', variant: 'destructive' });
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [idLavanderia, currentPage, itemsPerPage, searchTerm, toast]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  // Cargar nombre de lavandería
  useEffect(() => {
    if (idLavanderia) {
      fetchLaundryById(idLavanderia)
        .then((data) => setLavanderiaNombre(data?.nombre || ''))
        .catch(() => setLavanderiaNombre(''));
    }
  }, [idLavanderia]);

  // Cargar clientes del centro de lavado
  const loadClientes = async () => {
    setIsLoadingClientes(true);
    try {
      const response = await fetchClientesByLavanderia(idLavanderia, { limit: 200 });
      setClientes(response?.data || []);
    } catch (error) {
      console.error('Error al cargar clientes:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los clientes', variant: 'destructive' });
    } finally {
      setIsLoadingClientes(false);
    }
  };

  // Cargar empleados del centro de lavado
  const loadEmpleados = async () => {
    setIsLoadingEmpleados(true);
    try {
      const response = await fetchEmployees({
        limit: 200,
        offset: 1,
        query: { 'lavanderia.id': idLavanderia, estado: 'Activo' },
      });
      setEmpleados(response?.data || []);
    } catch (error) {
      console.error('Error al cargar empleados:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los empleados', variant: 'destructive' });
    } finally {
      setIsLoadingEmpleados(false);
    }
  };

  // Cargar prendas asignadas al cliente
  const loadPrendasCliente = async (clienteId) => {
    setIsLoadingPrendas(true);
    try {
      const response = await fetch(`${API_URL}/client-garments/client/${clienteId}`);
      if (!response.ok) throw new Error('Error al obtener prendas');
      const result = await response.json();
      const assignments = result?.data || [];
      setPrendas(
        assignments.map((a) => ({
          prendaId: a.prendaId || a._id,
          nombre: a.nombre,
          peso_teorico: a.peso || 0,
          cantidad: 0,
          peso_real: 0,
        }))
      );
    } catch (error) {
      console.error('Error al cargar prendas del cliente:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las prendas del cliente', variant: 'destructive' });
      setPrendas([]);
    } finally {
      setIsLoadingPrendas(false);
    }
  };

  // Abrir modal de crear
  const handleOpenCreate = () => {
    setSelectedCliente(null);
    setClienteSearch('');
    setPrendas([]);
    setSelectedEmpleado(null);
    setEmpleadoSearch('');
    setObservaciones('');
    setIsCreateOpen(true);
    loadClientes();
    loadEmpleados();
  };

  // Seleccionar cliente
  const handleSelectCliente = (cliente) => {
    setSelectedCliente(cliente);
    setClienteSearch('');
    loadPrendasCliente(cliente._id);
  };

  // Actualizar cantidad de prenda
  const updatePrendaCantidad = (index, value) => {
    setPrendas((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], cantidad: Math.max(0, parseInt(value) || 0) };
      return updated;
    });
  };

  // Actualizar peso real de prenda
  const updatePrendaPesoReal = (index, value) => {
    setPrendas((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], peso_real: Math.max(0, parseFloat(value) || 0) };
      return updated;
    });
  };

  // Tipo de cobro del cliente seleccionado
  const tipoCobro = selectedCliente?.tipoCobro || 'cantidades';

  // Calcular totales
  const getTotales = () => {
    let prendasActivas;
    let total_prendas, total_peso_real, total_peso_teorico;

    if (tipoCobro === 'kilos') {
      prendasActivas = prendas.filter((p) => p.peso_real > 0);
      total_prendas = prendasActivas.length;
      total_peso_real = prendasActivas.reduce((s, p) => s + p.peso_real, 0);
      total_peso_teorico = prendasActivas.reduce((s, p) => s + p.peso_teorico, 0);
    } else {
      prendasActivas = prendas.filter((p) => p.cantidad > 0);
      total_prendas = prendasActivas.reduce((s, p) => s + p.cantidad, 0);
      total_peso_real = prendasActivas.reduce((s, p) => s + p.peso_real, 0);
      total_peso_teorico = prendasActivas.reduce((s, p) => s + (p.peso_teorico * p.cantidad), 0);
    }
    return { total_prendas, total_peso_real, total_peso_teorico, prendasActivas };
  };

  // Guardar recepción
  const handleSave = async () => {
    if (!selectedCliente) {
      toast({ title: 'Advertencia', description: 'Selecciona un cliente', variant: 'destructive' });
      return;
    }
    if (!selectedEmpleado) {
      toast({ title: 'Advertencia', description: 'Selecciona el empleado que recibe', variant: 'destructive' });
      return;
    }

    const { total_prendas, total_peso_real, total_peso_teorico, prendasActivas } = getTotales();

    if (prendasActivas.length === 0) {
      const msg = tipoCobro === 'kilos'
        ? 'Ingresa al menos una prenda con peso'
        : 'Ingresa al menos una prenda con cantidad';
      toast({ title: 'Advertencia', description: msg, variant: 'destructive' });
      return;
    }

    const recibidoPorNombre = `${selectedEmpleado.nombre || ''} ${selectedEmpleado.apellido || ''}`.trim();

    setIsSaving(true);
    try {
      const data = {
        cliente: {
          id: selectedCliente._id,
          nombre: selectedCliente.nombre || selectedCliente.name || '',
          nit: selectedCliente.nit || '',
        },
        lavanderia: {
          id: idLavanderia,
          nombre: lavanderiaNombre || '',
        },
        tipoCobro,
        prendas: prendasActivas.map((p) => ({
          prendaId: p.prendaId,
          nombre: p.nombre,
          peso_teorico: tipoCobro === 'kilos' ? 0 : p.peso_teorico,
          cantidad: p.cantidad,
          peso_real: p.peso_real,
        })),
        total_prendas,
        total_peso_real,
        total_peso_teorico: tipoCobro === 'kilos' ? 0 : total_peso_teorico,
        recibido_por: recibidoPorNombre,
        observaciones: observaciones.trim(),
      };

      const response = await createRecepcionRopaCliente(data);

      if (response.state) {
        toast({ title: 'Recepción creada', description: `Consecutivo: ${response.data?.consecutivo}` });
        setIsCreateOpen(false);
        setReciboData(response.data);
        setIsReciboOpen(true);
        loadRecords();
      } else {
        toast({ title: 'Error', description: response.message || 'No se pudo crear la recepción', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudo crear la recepción', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // Imprimir recibo por bluetooth
  const handlePrint = async (dataToprint) => {
    const data = dataToprint || reciboData;
    if (!data) return;

    if (!printer.isBluetoothAvailable()) {
      toast({ title: 'No disponible', description: 'Tu navegador no soporta Bluetooth', variant: 'destructive' });
      return;
    }

    setIsPrinting(true);
    try {
      if (!printer.isConnected()) {
        toast({ title: 'Conectando...', description: 'Selecciona tu impresora Bluetooth' });
        await printer.connect();
      }

      toast({ title: 'Imprimiendo...', description: 'Imprimiendo recibo de recepción' });
      await printer.printRecepcionRopa(data);

      toast({ title: 'Éxito', description: `Recibo ${data.consecutivo} impreso correctamente` });
    } catch (error) {
      console.error('Error al imprimir recibo:', error);
      let errorMessage = 'No se pudo imprimir el recibo';
      if (error.message?.includes('User cancelled')) errorMessage = 'Impresión cancelada';
      else if (error.message?.includes('No hay conexión')) errorMessage = 'Impresora desconectada. Intenta de nuevo.';
      toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
    } finally {
      setIsPrinting(false);
    }
  };

  // Helpers
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.toLocaleDateString('es-ES')} ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const formatPeso = (gramos) => {
    if (!gramos) return '0 kg';
    return `${(gramos / 1000).toFixed(2)} kg`;
  };

  // Filtrar clientes por búsqueda
  const filteredClientes = clientes.filter((c) => {
    if (!clienteSearch) return true;
    const term = clienteSearch.toLowerCase();
    return (
      (c.nombre || c.name || '').toLowerCase().includes(term) ||
      (c.nit || '').toLowerCase().includes(term)
    );
  });

  // Filtrar empleados por búsqueda
  const filteredEmpleados = empleados.filter((e) => {
    if (!empleadoSearch) return true;
    const term = empleadoSearch.toLowerCase();
    const fullName = `${e.nombre || ''} ${e.apellido || ''}`.toLowerCase();
    return fullName.includes(term) || (e.cedula || '').toLowerCase().includes(term);
  });

  // Columnas de la tabla
  const tableColumns = [
    {
      id: 'actions',
      key: 'actions',
      label: 'Acciones',
      render: (row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-blue-600 hover:text-blue-700"
            title="Ver detalle"
            onClick={() => { setDetailRecord(row); setIsDetailOpen(true); }}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-600 hover:text-gray-700"
            title="Imprimir recibo"
            onClick={() => handlePrint(row)}
            disabled={isPrinting}
          >
            <Printer className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
    {
      id: 'consecutivo',
      label: 'Consecutivo',
      render: (row) => (
        <span className="font-semibold text-emerald-700">{row.consecutivo}</span>
      ),
    },
    {
      id: 'cliente',
      label: 'Cliente',
      render: (row) => (
        <div>
          <div className="font-medium">{row.cliente?.nombre}</div>
          {row.cliente?.nit && <div className="text-xs text-gray-500">{row.cliente.nit}</div>}
        </div>
      ),
    },
    {
      id: 'tipoCobro',
      label: 'Tipo Cobro',
      render: (row) => (
        <Badge className={row.tipoCobro === 'kilos' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}>
          {row.tipoCobro === 'kilos' ? 'Por Kilos' : 'Por Cantidades'}
        </Badge>
      ),
    },
    {
      id: 'total_prendas',
      label: 'Total Prendas',
      render: (row) => <span className="font-medium">{row.total_prendas || 0}</span>,
    },
    {
      id: 'total_peso_real',
      label: 'Peso total',
      render: (row) => <span className="text-sm">{formatPeso(row.total_peso_real)}</span>,
    },
    {
      id: 'total_peso_teorico',
      label: 'Peso Teórico',
      render: (row) => <span className="text-sm">{formatPeso(row.total_peso_teorico)}</span>,
    },
    {
      id: 'recibido_por',
      label: 'Recibió',
      render: (row) => <span className="text-sm">{row.recibido_por || '-'}</span>,
    },
    {
      id: 'fecha_recepcion',
      label: 'Fecha Recepción',
      render: (row) => (
        <span className="text-sm">{formatDateTime(row.fecha_recepcion || row.createdAt)}</span>
      ),
    },
  ];

  // Columnas para exportar a Excel
  const excelColumns = [
    { key: 'consecutivo', header: 'Consecutivo' },
    { key: 'cliente.nombre', header: 'Cliente' },
    { key: 'cliente.nit', header: 'NIT' },
    { key: 'tipoCobro', header: 'Tipo Cobro' },
    { key: 'total_prendas', header: 'Total Prendas' },
    { key: 'total_peso_real', header: 'Peso Total (kg)' },
    { key: 'total_peso_teorico', header: 'Peso Teórico (kg)' },
    { key: 'recibido_por', header: 'Recibido Por' },
    { key: 'fecha_recepcion', header: 'Fecha Recepción', formatDate: true },
    { key: 'observaciones', header: 'Observaciones' },
  ];

  const { total_prendas: formTotalPrendas, total_peso_real: formTotalPesoReal, total_peso_teorico: formTotalPesoTeorico } = getTotales();

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6">
      <div className="mb-4">
        <Button asChild variant="outline">
          <Link to={`/centros-lavado/${idLavanderia}`}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver al Centro de Lavado
          </Link>
        </Button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-500/10 p-3 rounded-lg">
                  <Package className="h-8 w-8 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Recibir Ropa Clientes</CardTitle>
                  <CardDescription>
                    Registrar recepción de prendas de clientes
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ExportExcelButton
                  service={fetchRecepcionesRopaClientes}
                  filters={{ 'lavanderia.id': idLavanderia }}
                  columns={excelColumns}
                  fileName={`recepciones-ropa-clientes-${new Date().toISOString().slice(0, 10)}`}
                />
                <Button
                  onClick={handleOpenCreate}
                  className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus className="h-4 w-4" />
                  Recibir Ropa
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Buscar por cliente, consecutivo o quien recibe..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Tabla */}
            <DataTable
              data={records}
              columns={tableColumns}
              isLoading={isLoading}
              page={currentPage}
              limit={itemsPerPage}
              totalRecords={totalRecords}
              onPageChange={setCurrentPage}
              onLimitChange={setItemsPerPage}
            />
          </CardContent>
        </Card>
      </motion.div>

      {/* ========== Modal: Crear Recepción ========== */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-emerald-600" />
              Registrar Recepción de Ropa
            </DialogTitle>
            <DialogDescription>
              Selecciona el cliente y registra las prendas recibidas
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4 pr-1">
            {/* Selección de cliente */}
            {!selectedCliente ? (
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Seleccionar Cliente</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Buscar cliente por nombre o NIT..."
                    value={clienteSearch}
                    onChange={(e) => setClienteSearch(e.target.value)}
                    className="pl-10"
                    autoFocus
                  />
                </div>
                {isLoadingClientes ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                    <span className="ml-2 text-sm text-gray-500">Cargando clientes...</span>
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
                    {filteredClientes.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">No se encontraron clientes</div>
                    ) : (
                      filteredClientes.map((c) => (
                        <button
                          key={c._id}
                          onClick={() => handleSelectCliente(c)}
                          className="w-full text-left p-3 hover:bg-emerald-50 transition-colors flex items-center justify-between"
                        >
                          <div>
                            <p className="font-medium text-sm">{c.nombre || c.name}</p>
                            {c.nit && <p className="text-xs text-gray-500">NIT: {c.nit}</p>}
                          </div>
                          <Badge className="bg-emerald-100 text-emerald-800 text-xs">Seleccionar</Badge>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Cliente seleccionado */}
                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <div>
                    <p className="text-xs text-emerald-600 mb-1">Cliente seleccionado</p>
                    <p className="font-semibold text-sm">{selectedCliente.nombre || selectedCliente.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {selectedCliente.nit && <span className="text-xs text-gray-500">NIT: {selectedCliente.nit}</span>}
                      <Badge className={tipoCobro === 'kilos' ? 'bg-blue-100 text-blue-800 text-xs' : 'bg-purple-100 text-purple-800 text-xs'}>
                        {tipoCobro === 'kilos' ? 'Cobro por Kilos' : 'Cobro por Cantidades'}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-emerald-600 hover:text-emerald-800"
                    onClick={() => { setSelectedCliente(null); setPrendas([]); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {/* Tabla de prendas */}
                {isLoadingPrendas ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
                    <span className="ml-2 text-sm text-gray-500">Cargando prendas...</span>
                  </div>
                ) : prendas.length === 0 ? (
                  <div className="text-center py-8 text-sm text-gray-500">
                    Este cliente no tiene prendas parametrizadas
                  </div>
                ) : (
                  <div>
                    <Label className="text-sm font-semibold mb-2 block">
                      Prendas del cliente — {tipoCobro === 'kilos' ? 'ingresar peso (kg)' : 'ingresar cantidades'}
                    </Label>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-4 py-2 font-semibold">Prenda</th>
                            {tipoCobro !== 'kilos' && (
                              <th className="text-center px-4 py-2 font-semibold">Peso Unit. (kg)</th>
                            )}
                            {tipoCobro !== 'kilos' && (
                              <th className="text-center px-4 py-2 font-semibold">Cantidad</th>
                            )}
                            <th className="text-center px-4 py-2 font-semibold">Peso (kg)</th>
                            {tipoCobro !== 'kilos' && (
                              <th className="text-center px-4 py-2 font-semibold">Peso Teórico</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {prendas.map((item, idx) => (
                            <tr key={idx} className="hover:bg-gray-50/50">
                              <td className="px-4 py-2 font-medium">{item.nombre}</td>
                              {tipoCobro !== 'kilos' && (
                                <td className="px-4 py-2 text-center text-gray-500">{(item.peso_teorico / 1000).toFixed(2)}</td>
                              )}
                              {tipoCobro !== 'kilos' && (
                                <td className="px-4 py-2">
                                  <Input
                                    type="number"
                                    className="w-20 h-8 text-center text-sm mx-auto"
                                    value={item.cantidad || ''}
                                    onChange={(e) => {
                                      updatePrendaCantidad(idx, e.target.value);
                                      const cant = Math.max(0, parseInt(e.target.value) || 0);
                                      setPrendas((prev) => {
                                        const updated = [...prev];
                                        updated[idx] = { ...updated[idx], peso_real: item.peso_teorico * cant };
                                        return updated;
                                      });
                                    }}
                                    min={0}
                                    placeholder="0"
                                  />
                                </td>
                              )}
                              <td className="px-4 py-2">
                                {tipoCobro === 'kilos' ? (
                                  <Input
                                    type="number"
                                    className="w-24 h-8 text-center text-sm mx-auto"
                                    value={item.peso_real ? (item.peso_real / 1000) : ''}
                                    onChange={(e) => {
                                      const kg = Math.max(0, parseFloat(e.target.value) || 0);
                                      updatePrendaPesoReal(idx, kg * 1000);
                                    }}
                                    min={0}
                                    placeholder="0"
                                    step="0.01"
                                  />
                                ) : (
                                  <span className="block text-center text-gray-500">{formatPeso(item.peso_real)}</span>
                                )}
                              </td>
                              {tipoCobro !== 'kilos' && (
                                <td className="px-4 py-2 text-center text-gray-500">
                                  {formatPeso(item.peso_teorico * item.cantidad)}
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Totales */}
                    {tipoCobro === 'kilos' ? (
                      <div className="mt-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                          <p className="text-xs text-blue-600">Peso Total</p>
                          <p className="text-lg font-bold text-blue-800">{formatPeso(formTotalPesoReal)}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-3 mt-3">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                          <p className="text-xs text-emerald-600">Total Prendas</p>
                          <p className="text-lg font-bold text-emerald-800">{formTotalPrendas}</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                          <p className="text-xs text-blue-600">Peso Total</p>
                          <p className="text-lg font-bold text-blue-800">{formatPeso(formTotalPesoReal)}</p>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                          <p className="text-xs text-gray-600">Peso Teórico</p>
                          <p className="text-lg font-bold text-gray-800">{formatPeso(formTotalPesoTeorico)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Quien recibe (selector de empleado) + observaciones */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-semibold">
                      Quien recibe <span className="text-red-500">*</span>
                    </Label>
                    {selectedEmpleado ? (
                      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-2 mt-1">
                        <div>
                          <p className="text-sm font-medium">{selectedEmpleado.nombre} {selectedEmpleado.apellido}</p>
                          {selectedEmpleado.cedula && <p className="text-xs text-gray-500">CC: {selectedEmpleado.cedula}</p>}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-blue-600 hover:text-blue-800"
                          onClick={() => setSelectedEmpleado(null)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-1 space-y-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                          <Input
                            placeholder="Buscar empleado..."
                            value={empleadoSearch}
                            onChange={(e) => setEmpleadoSearch(e.target.value)}
                            className="pl-8 h-8 text-sm"
                          />
                        </div>
                        {isLoadingEmpleados ? (
                          <div className="flex items-center justify-center py-3">
                            <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                            <span className="ml-2 text-xs text-gray-500">Cargando...</span>
                          </div>
                        ) : (
                          <div className="max-h-32 overflow-y-auto border rounded-lg divide-y">
                            {filteredEmpleados.length === 0 ? (
                              <div className="p-2 text-center text-xs text-gray-500">No se encontraron empleados</div>
                            ) : (
                              filteredEmpleados.map((e) => (
                                <button
                                  key={e._id}
                                  onClick={() => { setSelectedEmpleado(e); setEmpleadoSearch(''); }}
                                  className="w-full text-left p-2 hover:bg-blue-50 transition-colors text-sm"
                                >
                                  <span className="font-medium">{e.nombre} {e.apellido}</span>
                                  {e.cedula && <span className="text-xs text-gray-500 ml-2">CC: {e.cedula}</span>}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="observaciones" className="text-sm font-semibold">Observaciones</Label>
                    <textarea
                      id="observaciones"
                      placeholder="Observaciones (opcional)"
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      rows={3}
                      className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsCreateOpen(false)} disabled={isSaving}>
              Cancelar
            </Button>
            {selectedCliente && (
              <Button onClick={handleSave} disabled={isSaving} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Guardar Recepción
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== Modal: Recibo / Resumen ========== */}
      <Dialog open={isReciboOpen} onOpenChange={setIsReciboOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5 text-emerald-600" />
              Recibo de Recepción — {reciboData?.consecutivo || 'N/A'}
            </DialogTitle>
            <DialogDescription>
              Resumen de la recepción registrada
            </DialogDescription>
          </DialogHeader>

          {reciboData && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Consecutivo</p>
                  <p className="text-sm font-semibold text-emerald-700">{reciboData.consecutivo}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Fecha</p>
                  <p className="text-sm font-medium">{formatDateTime(reciboData.fecha_recepcion || reciboData.createdAt)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Cliente</p>
                  <p className="text-sm font-medium">{reciboData.cliente?.nombre}</p>
                  {reciboData.cliente?.nit && <p className="text-xs text-gray-400">{reciboData.cliente.nit}</p>}
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Recibido por</p>
                  <p className="text-sm font-medium">{reciboData.recibido_por}</p>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Prenda</th>
                      {reciboData.tipoCobro !== 'kilos' && (
                        <th className="text-center px-4 py-2 font-semibold">Cantidad</th>
                      )}
                      <th className="text-center px-4 py-2 font-semibold">Peso (kg)</th>
                      {reciboData.tipoCobro !== 'kilos' && (
                        <th className="text-center px-4 py-2 font-semibold">Peso Teórico</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(reciboData.prendas || []).map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50/50">
                        <td className="px-4 py-2 font-medium">{item.nombre}</td>
                        {reciboData.tipoCobro !== 'kilos' && (
                          <td className="px-4 py-2 text-center">{item.cantidad}</td>
                        )}
                        <td className="px-4 py-2 text-center">{formatPeso(item.peso_real)}</td>
                        {reciboData.tipoCobro !== 'kilos' && (
                          <td className="px-4 py-2 text-center">{formatPeso(item.peso_teorico * item.cantidad)}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {reciboData.tipoCobro === 'kilos' ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-blue-600">Peso Total</p>
                  <p className="text-lg font-bold text-blue-800">{formatPeso(reciboData.total_peso_real)}</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-emerald-600">Total Prendas</p>
                    <p className="text-lg font-bold text-emerald-800">{reciboData.total_prendas}</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-blue-600">Peso Total</p>
                    <p className="text-lg font-bold text-blue-800">{formatPeso(reciboData.total_peso_real)}</p>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-600">Peso Teórico</p>
                    <p className="text-lg font-bold text-gray-800">{formatPeso(reciboData.total_peso_teorico)}</p>
                  </div>
                </div>
              )}

              {reciboData.observaciones && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-600 mb-1">Observaciones</p>
                  <p className="text-sm">{reciboData.observaciones}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsReciboOpen(false)}>Cerrar</Button>
            <Button
              onClick={() => handlePrint(reciboData)}
              disabled={isPrinting}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              {isPrinting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              {isPrinting ? 'Imprimiendo...' : 'Imprimir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== Modal: Detalle ========== */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-emerald-600" />
              Detalle Recepción — {detailRecord?.consecutivo || 'N/A'}
            </DialogTitle>
            <DialogDescription>
              Cliente: {detailRecord?.cliente?.nombre || 'N/A'}
            </DialogDescription>
          </DialogHeader>

          {detailRecord && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Consecutivo</p>
                  <p className="text-sm font-semibold text-emerald-700">{detailRecord.consecutivo}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Cliente</p>
                  <p className="text-sm font-medium">{detailRecord.cliente?.nombre}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Recibido por</p>
                  <p className="text-sm font-medium">{detailRecord.recibido_por || '-'}</p>
                </div>
                {detailRecord.tipoCobro !== 'kilos' && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Total Prendas</p>
                    <p className="text-sm font-bold">{detailRecord.total_prendas}</p>
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Peso Total</p>
                  <p className="text-sm font-medium">{formatPeso(detailRecord.total_peso_real)}</p>
                </div>
                {detailRecord.tipoCobro !== 'kilos' && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Peso Teórico</p>
                    <p className="text-sm font-medium">{formatPeso(detailRecord.total_peso_teorico)}</p>
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg p-3 col-span-2 sm:col-span-3">
                  <p className="text-xs text-gray-500 mb-1">Fecha Recepción</p>
                  <p className="text-sm font-medium">{formatDateTime(detailRecord.fecha_recepcion || detailRecord.createdAt)}</p>
                </div>
              </div>

              {/* Prendas */}
              <div>
                <Label className="text-sm font-semibold mb-2 block">Prendas recibidas</Label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold">Prenda</th>
                        {detailRecord.tipoCobro !== 'kilos' && (
                          <th className="text-center px-4 py-2 font-semibold">Cantidad</th>
                        )}
                        <th className="text-center px-4 py-2 font-semibold">Peso (kg)</th>
                        {detailRecord.tipoCobro !== 'kilos' && (
                          <th className="text-center px-4 py-2 font-semibold">Peso Teórico</th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {(detailRecord.prendas || []).map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2 font-medium">{item.nombre}</td>
                          {detailRecord.tipoCobro !== 'kilos' && (
                            <td className="px-4 py-2 text-center">{item.cantidad}</td>
                          )}
                          <td className="px-4 py-2 text-center">{formatPeso(item.peso_real)}</td>
                          {detailRecord.tipoCobro !== 'kilos' && (
                            <td className="px-4 py-2 text-center">{formatPeso(item.peso_teorico * item.cantidad)}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {detailRecord.observaciones && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-xs text-yellow-600 mb-1">Observaciones</p>
                  <p className="text-sm">{detailRecord.observaciones}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailOpen(false)}>Cerrar</Button>
            <Button
              onClick={() => handlePrint(detailRecord)}
              disabled={isPrinting}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
            >
              {isPrinting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              {isPrinting ? 'Imprimiendo...' : 'Imprimir Recibo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecepcionRopaClientesPage;
